export const PRO_SEAL_WIDGET_ID = "73bdf96f-066d-47fb-8611-77958c19ff4f";
export const PRO_SEAL_SCRIPT_URL = "https://s.provenexpert.net/seals/proseal-v2.js";
export const PRO_SEAL_DATA_URL = `https://d.provenexpert.net/v2/seals/${PRO_SEAL_WIDGET_ID}/public?x-pe-user-agent=`;
export const PRO_SEAL_PROFILE_URL =
	"https://www.provenexpert.com/bergdorfbio-hochreine-peptide-forschungsbedarf/" +
	"?utm_source=seals&utm_campaign=embedded-proseal&utm_medium=profile" +
	`&utm_content=${PRO_SEAL_WIDGET_ID}`;

const LANGUAGE_MAP: Record<string, string> = {
	de: "de-DE",
	en: "en-US",
	it: "it-IT",
	es: "es-ES",
	fr: "fr-FR",
	nl: "nl-NL",
	pt: "pt-PT",
	pl: "pl-PL",
	sv: "sv-SE",
	da: "da-DK",
	el: "el-GR",
	cs: "cs-CZ",
	fi: "fi-FI",
	ro: "ro-RO",
	hu: "hu-HU",
	sk: "sk-SK",
	tr: "tr-TR",
	nb: "nb-NO",
	bg: "bg-BG",
	hr: "hr-HR",
};

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Pick<Response, "ok" | "json">>;

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

export function resolveProSealLanguage(locale: string): string {
	return LANGUAGE_MAP[locale] ?? "en-US";
}

export function isActiveProSealPayload(payload: unknown): boolean {
	if (!isRecord(payload) || !isRecord(payload.data) || !isRecord(payload.data.attributes)) return false;

	const attributes = payload.data.attributes;
	return (
		attributes._type === "proseal" && attributes._id === PRO_SEAL_WIDGET_ID && attributes.active === true
	);
}

/**
 * ProvenExpert returns HTTP 200 even when a seal is disabled. The `active` field
 * is therefore the only safe gate before executing their widget runtime.
 */
export async function isProSealAvailable(signal?: AbortSignal, fetcher: FetchLike = fetch): Promise<boolean> {
	try {
		const response = await fetcher(PRO_SEAL_DATA_URL, {
			cache: "no-store",
			credentials: "omit",
			mode: "cors",
			signal,
		});
		if (!response.ok) return false;

		return isActiveProSealPayload(await response.json());
	} catch {
		return false;
	}
}

const PRO_SEAL_SOURCE_PATTERN = /(?:https:\/\/s\.provenexpert\.net)?\/seals\/proseal-v2\.js(?:[?:#]|$)/i;

function frameSources(frame: unknown): string[] {
	if (!isRecord(frame)) return [];
	return [frame.filename, frame.abs_path].filter((value): value is string => typeof value === "string");
}

/**
 * Drops only exception events whose identifiable stack sources all belong to
 * ProvenExpert. Mixed or source-less stacks remain visible in PostHog.
 */
export function isProSealOnlyExceptionEvent(event: unknown): boolean {
	if (!isRecord(event) || event.event !== "$exception" || !isRecord(event.properties)) return false;

	const exceptionList = event.properties.$exception_list;
	if (!Array.isArray(exceptionList) || exceptionList.length === 0) return false;

	let hasProSealFrame = false;
	for (const exception of exceptionList) {
		if (
			!isRecord(exception) ||
			!isRecord(exception.stacktrace) ||
			!Array.isArray(exception.stacktrace.frames)
		) {
			return false;
		}

		const sources = exception.stacktrace.frames.flatMap(frameSources);
		if (sources.length === 0) return false;
		if (sources.some((source) => !PRO_SEAL_SOURCE_PATTERN.test(source))) return false;
		hasProSealFrame = true;
	}

	return hasProSealFrame;
}

/**
 * Manually checked state of the public ProvenExpert profile. These numbers are NOT live:
 * the widget fetches its own data at runtime, this snapshot exists so server-rendered
 * surfaces (LP header, stat band, reviews headline) can print a rating without a network
 * call. Update `checkedAt` whenever `rating` or `reviewCount` changes.
 */
export const PRO_SEAL_PUBLIC_SNAPSHOT = { rating: 4.8, reviewCount: 36, checkedAt: "2026-09-03" } as const;
